"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.machineJSON = exports.createAppointmentCancelMachine = void 0;
const xstate_1 = require("xstate");
const moment_1 = __importDefault(require("moment"));
const xstate_2 = require("../../lib/constants/xstate");
const API_1 = require("../../lib/API");
const utils_1 = require("../../lib/utils");
const getInitialContext_1 = __importDefault(require("../getInitialContext"));
const APPOINTMENT_CANCELED_VALUE = 'canceled';
/**
 * Creates and returns the machine that orchestrates the appointment-cancel component.
 * @param {object} initialContext the initial context of the machine
 * @returns xstate.Machine
 */
const createAppointmentCancelMachine = (initialContext = (0, getInitialContext_1.default)()) => {
    return (0, xstate_1.Machine)((0, exports.machineJSON)(initialContext), {
        actions: {
            [xstate_2.actions.setAppointment]: (0, xstate_1.assign)({
                appointment: (_context, event) => {
                    return event.data.appointment;
                },
                locationId: (_context, event) => {
                    return event.data.locationId;
                },
                providerId: (_context, event) => {
                    return event.data.providerId;
                }
            }),
            [xstate_2.actions.setProvider]: (0, xstate_1.assign)({
                provider: (_context, event) => {
                    return event.data.provider;
                },
                locations: (_context, event) => {
                    return event.data.locations;
                }
            }),
            [xstate_2.actions.setCancelParameters]: (0, xstate_1.assign)({
                cancelEmailConsent: (_context, event) => {
                    return event.data.cancelEmailConsent;
                },
                cancelReasonText: (_context, event) => {
                    return event.data.cancelReasonText;
                }
            })
        },
        guards: {
            [xstate_2.guards.hasAppointment]: (context) => {
                return !!context.appointment;
            },
            [xstate_2.guards.isAppointmentCanceled]: (context) => {
                return context.appointment.status === APPOINTMENT_CANCELED_VALUE;
            },
            [xstate_2.guards.isCancelationDisabled]: (context) => {
                return !context.appointment.isCancelEnabled;
            },
            [xstate_2.guards.isCancelationExpired]: (context) => {
                return !!(0, moment_1.default)(context.appointment.latestCancellation).isBefore((0, moment_1.default)());
            },
            [xstate_2.guards.shouldSkipProvider]: (context) => {
                return !context.services.getProvider;
            },
            [xstate_2.guards.wasNotInitializedWithAppointmentData]: (context) => {
                return !context.appointment && !context.appointmentId;
            },
            [xstate_2.guards.hasAppointmentBeenMissed]: (context) => {
                var _a;
                const appointmentDate = (_a = context.appointment) === null || _a === void 0 ? void 0 : _a.startDateTime;
                return !!appointmentDate && (0, moment_1.default)(appointmentDate).isBefore((0, moment_1.default)());
            }
        },
        services: {
            [xstate_2.services.getAppointment]: (context) => __awaiter(void 0, void 0, void 0, function* () {
                return (0, API_1.getAppointmentById)(context.services.getAppointment, context.appointmentId);
            }),
            [xstate_2.services.getProvider]: (context) => __awaiter(void 0, void 0, void 0, function* () {
                return (0, API_1.getProviderById)(context.services.getProvider, context.providerId);
            }),
            [xstate_2.services.deleteAppointment]: (context) => __awaiter(void 0, void 0, void 0, function* () {
                return (0, API_1.cancelAppointmentById)(context.services.cancelAppointment, context.appointmentId, context.cancelEmailConsent, context.cancelReasonText);
            })
        }
    });
};
exports.createAppointmentCancelMachine = createAppointmentCancelMachine;
/**
 * Returns the machine JSON for appointment-cancel. Exported to facilitate testing.
 * @param {object} initialContext the initial context for the machine
 * @returns object
 */
const machineJSON = (initialContext = (0, getInitialContext_1.default)()) => {
    return {
        id: 'appointment-cancel',
        initial: xstate_2.states.initializing,
        context: Object.assign(Object.assign({}, initialContext), (0, utils_1.convertAppointment)(initialContext.appointment)),
        states: {
            [xstate_2.states.initializing]: {
                on: {
                    [xstate_2.events.initialized]: {
                        target: xstate_2.states.appointment
                    }
                }
            },
            [xstate_2.states.appointment]: {
                initial: xstate_2.states.routing,
                states: {
                    [xstate_2.states.routing]: {
                        always: [
                            {
                                cond: xstate_2.guards.wasNotInitializedWithAppointmentData,
                                target: xstate_2.states.error
                            },
                            { cond: xstate_2.guards.hasAppointment, target: xstate_2.states.success },
                            { target: xstate_2.states.pending }
                        ]
                    },
                    [xstate_2.states.pending]: {
                        invoke: {
                            id: 'getAppointment',
                            src: xstate_2.services.getAppointment,
                            onDone: {
                                actions: xstate_2.actions.setAppointment,
                                target: xstate_2.states.success
                            },
                            onError: {
                                target: xstate_2.states.error
                            }
                        }
                    },
                    [xstate_2.states.success]: {
                        type: 'final'
                    },
                    [xstate_2.states.error]: {
                    // Dead end
                    }
                },
                onDone: {
                    target: xstate_2.states.provider
                }
            },
            [xstate_2.states.provider]: {
                initial: xstate_2.states.routing,
                states: {
                    [xstate_2.states.routing]: {
                        always: [
                            {
                                cond: xstate_2.guards.shouldSkipProvider,
                                target: xstate_2.states.success
                            },
                            { target: xstate_2.states.pending }
                        ]
                    },
                    [xstate_2.states.pending]: {
                        invoke: {
                            id: 'getProvider',
                            src: xstate_2.services.getProvider,
                            onDone: {
                                actions: xstate_2.actions.setProvider,
                                target: xstate_2.states.success
                            },
                            onError: {
                                // Should target loadingProviderError, current designs are all or nothing
                                target: xstate_2.states.error
                            }
                        }
                    },
                    [xstate_2.states.success]: {
                        type: 'final'
                    },
                    [xstate_2.states.error]: {
                    // Dead end
                    }
                },
                onDone: [
                    {
                        cond: xstate_2.guards.isAppointmentCanceled,
                        target: xstate_2.states.canceledPreviously
                    },
                    {
                        cond: xstate_2.guards.hasAppointmentBeenMissed,
                        target: xstate_2.states.appointmentMissed
                    },
                    {
                        cond: xstate_2.guards.isCancelationDisabled,
                        target: xstate_2.states.cancelDisabled
                    },
                    {
                        target: xstate_2.states.cancelPage
                    }
                ]
            },
            [xstate_2.states.cancelDisabled]: {
                initial: xstate_2.states.routing,
                states: {
                    [xstate_2.states.routing]: {
                        always: [
                            {
                                cond: xstate_2.guards.isCancelationExpired,
                                target: xstate_2.states.cancelExpiredPage
                            },
                            {
                                target: xstate_2.states.cancelDisabledPage
                            }
                        ]
                    },
                    [xstate_2.states.cancelDisabledPage]: {
                    // Dead end
                    },
                    [xstate_2.states.cancelExpiredPage]: {
                    // Dead end
                    }
                }
            },
            [xstate_2.states.appointmentMissed]: {
                type: 'final'
            },
            [xstate_2.states.canceledPreviously]: {
            // Dead end
            },
            [xstate_2.states.cancelPage]: {
                initial: xstate_2.states.appointmentCancel,
                states: {
                    [xstate_2.states.appointmentCancel]: {
                        on: {
                            [xstate_2.events.cancel]: {
                                target: xstate_2.states.cancelingAppointment,
                                actions: xstate_2.actions.setCancelParameters
                            }
                        }
                    },
                    [xstate_2.states.cancelingAppointment]: {
                        invoke: {
                            id: 'deleteAppointment',
                            src: xstate_2.services.deleteAppointment,
                            onDone: {
                                target: xstate_2.states.cancelFinished
                            },
                            onError: {
                                target: xstate_2.states.cancelError
                            }
                        }
                    },
                    [xstate_2.states.cancelError]: {
                    // Dead end
                    },
                    [xstate_2.states.cancelFinished]: {
                        type: 'final'
                    }
                },
                onDone: {
                    target: xstate_2.states.confirmationPage
                }
            },
            [xstate_2.states.confirmationPage]: {
                type: 'final'
            }
        }
    };
};
exports.machineJSON = machineJSON;
//# sourceMappingURL=main.js.map